import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/tw-merge/cn';
import type { NewsItem } from '@/types/blog';
import { getBlogTagColor } from '@/utils/blog-tag-colors';

type NewsCardProps = {
  item: NewsItem;
  theme?: 'light' | 'dark';
  variant?: 'default' | 'hero' | 'light';
  className?: string;
};

function getTagClasses(variant: 'default' | 'hero' | 'light') {
  const base =
    'flex flex-col items-center justify-center border-border-default capitalize h-[37px] rounded-full border bg-[#f7f7f7] px-3 py-1 text-[10.8px] font-semibold leading-none';

  return cn(base, variant === 'hero' && 'bg-white');
}

export function NewsCard({
  item,
  theme = 'light',
  variant = 'default',
  className,
}: NewsCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const isHeroVariant = variant === 'hero';
  const isLightVariant = variant === 'light';
  const hasDarkText = isLightVariant || (!isHeroVariant && !isDark);
  const cutoutColor = isLightVariant ? '#f7f7f7' : 'white';

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [6, -6]), {
    stiffness: 200,
    damping: 20,
  });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-6, 6]), {
    stiffness: 200,
    damping: 20,
  });
  const scale = useSpring(1, { stiffness: 200, damping: 20 });

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  }

  function handleMouseEnter() {
    scale.set(1.02);
  }

  function handleMouseLeave() {
    mouseX.set(0);
    mouseY.set(0);
    scale.set(1);
  }

  function navigateToHref() {
    if (item.href) {
      navigate(item.href);
    }
  }

  function handleCardKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (!item.href) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(item.href);
    }
  }

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={navigateToHref}
      onKeyDown={handleCardKeyDown}
      role={item.href ? 'link' : undefined}
      tabIndex={item.href ? 0 : undefined}
      style={{ rotateX, rotateY, scale, transformPerspective: 800 }}
      className={cn(
        'group relative flex cursor-pointer flex-col overflow-hidden rounded-[19.71px] sm:rounded-[24px]',
        isHeroVariant
          ? 'h-auto border border-border-default text-white lg:h-[574px]'
          : isLightVariant
            ? 'h-full bg-white text-foreground transition-colors duration-500 hover:bg-white'
            : 'h-full bg-[#f5f5f7]',
        isDark && !isHeroVariant && 'text-white',
        hasDarkText && 'text-foreground',
        className
      )}
    >
      <div
        className={cn(
          'w-full overflow-hidden',
          isHeroVariant
            ? 'h-[180px] sm:h-[220px] lg:h-[272px]'
            : 'h-[180px] sm:h-[220px]'
        )}
      >
        <img
          src={item.image}
          alt=""
          width={770}
          height={440}
          loading={isHeroVariant ? 'eager' : 'lazy'}
          decoding="async"
          className="h-full w-full object-cover"
        />
      </div>

      <div className="relative z-10 flex flex-1 flex-col p-5">
        <div className="mb-4 flex flex-wrap gap-2">
          {item.tags.map(tag => (
            <span
              key={tag}
              className={getTagClasses(variant)}
              style={{ color: getBlogTagColor(tag) }}
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <img
              src={item.avatar}
              alt={item.author}
              width={40}
              height={40}
              loading="lazy"
              decoding="async"
              className="h-5 w-5 flex-shrink-0 rounded-full object-cover"
            />
            <span
              className={cn(
                'truncate font-fraunces text-sm leading-[18px]',
                hasDarkText ? 'text-foreground' : 'text-white'
              )}
            >
              By {item.author}
            </span>
          </div>
          {item.timestamp ? (
            <span
              className={cn(
                'flex-shrink-0 text-[11px] leading-[16.5px] tracking-[0.34px]',
                hasDarkText ? 'text-foreground-secondary' : 'text-white/70'
              )}
            >
              {item.timestamp}
            </span>
          ) : null}
        </div>

        <h3
          title={item.title}
          className={cn(
            'mb-2 line-clamp-2 font-fraunces text-base font-medium leading-[23px] sm:text-xl sm:leading-[28px]',
            hasDarkText ? 'text-foreground' : 'text-white'
          )}
        >
          {item.title}
        </h3>

        <p
          className={cn(
            'mb-5 line-clamp-3 text-sm leading-[21px]',
            hasDarkText ? 'text-foreground-secondary' : 'text-white/84'
          )}
        >
          {item.description}
        </p>

        <div
          className={cn(
            'mt-4 flex items-center justify-between sm:mt-6',
            hasDarkText ? 'text-foreground' : 'text-white'
          )}
        >
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              navigateToHref();
            }}
            className="text-sm font-medium"
          >
            Read more
          </button>
        </div>
      </div>

      {!isHeroVariant && (
        <div
          className="absolute bottom-0 right-0 flex h-[70px] w-[70px] items-center justify-center rounded-tl-[20px] opacity-100 transition-opacity duration-700 ease-in-out group-hover:opacity-0"
          style={{ backgroundColor: cutoutColor }}
        >
          <div
            className="absolute right-0 top-[-20px] h-5 w-5"
            style={{
              background: `radial-gradient(circle at top left, transparent 20px, ${cutoutColor} 20px)`,
            }}
          />
          <div
            className="absolute bottom-0 left-[-20px] h-5 w-5"
            style={{
              background: `radial-gradient(circle at top left, transparent 20px, ${cutoutColor} 20px)`,
            }}
          />
          <ArrowRight className="h-6 w-6 text-black" />
        </div>
      )}

      <div className="absolute bottom-7 right-7 z-50 translate-x-[-6px] opacity-0 transition-all duration-700 ease-in-out group-hover:translate-x-0 group-hover:opacity-100">
        <ArrowRight
          className={cn(
            'h-6 w-6',
            hasDarkText ? 'text-foreground' : 'text-white'
          )}
        />
      </div>
    </motion.div>
  );
}

export function BlogCardSkeleton({
  variant = 'default',
  className,
}: {
  variant?: 'default' | 'hero';
  className?: string;
}) {
  const isHeroVariant = variant === 'hero';

  return (
    <div
      className={cn(
        'relative flex animate-pulse flex-col overflow-hidden rounded-[19.71px] bg-[#f5f5f7] sm:rounded-[24px]',
        isHeroVariant
          ? 'h-auto border border-border-default lg:h-[574px]'
          : 'h-full',
        className
      )}
    >
      <div
        className={cn(
          'w-full bg-[#e8e8ed]',
          isHeroVariant
            ? 'h-[180px] sm:h-[220px] lg:h-[272px]'
            : 'h-[180px] sm:h-[220px]'
        )}
      />
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-4 flex flex-wrap gap-2">
          <div className="h-7 w-12 rounded-full bg-[#e8e8ed]" />
          <div className="h-7 w-28 rounded-full bg-[#e8e8ed]" />
          <div className="h-7 w-20 rounded-full bg-[#e8e8ed]" />
        </div>
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 rounded-full bg-[#e8e8ed]" />
            <div className="h-4 w-20 rounded-full bg-[#e8e8ed]" />
          </div>
          <div className="h-4 w-28 rounded-full bg-[#e8e8ed]" />
        </div>
        <div className="mb-2 h-7 w-full rounded-full bg-[#e8e8ed]" />
        <div className="mb-4 h-7 w-4/5 rounded-full bg-[#e8e8ed]" />
        <div className="mb-2 h-4 w-full rounded-full bg-[#e8e8ed]" />
        <div className="mb-2 h-4 w-[92%] rounded-full bg-[#e8e8ed]" />
        <div className="mb-5 h-4 w-[75%] rounded-full bg-[#e8e8ed]" />
        <div className="mt-auto h-4 w-20 rounded-full bg-[#e8e8ed]" />
      </div>
      {!isHeroVariant && (
        <div
          className="absolute bottom-0 right-0 flex h-[70px] w-[70px] items-center justify-center rounded-tl-[20px]"
          style={{ backgroundColor: 'var(--color-white)' }}
        >
          <div
            className="absolute right-0 top-[-20px] h-5 w-5"
            style={{
              background:
                'radial-gradient(circle at top left, transparent 20px, var(--color-white) 20px)',
            }}
          />
          <div
            className="absolute bottom-0 left-[-20px] h-5 w-5"
            style={{
              background:
                'radial-gradient(circle at top left, transparent 20px, var(--color-white) 20px)',
            }}
          />
        </div>
      )}
    </div>
  );
}
